import {
  Component,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Site } from '../../../models/site';
import { InspectionModule } from '../../../enums/inspection-module';
import { DrawingMode } from '../../../enums/drawing-mode';
import { selectDrawingMode } from '../../../features/cesium/cesium.selectors';
import { setDrawingMode } from '../../../features/cesium/cesium.actions';
import { Actions, ofType } from '@ngrx/effects';
import {
  addMeasureArea,
  addMeasureDistance, addMeasureFailure, addMeasureSuccess
} from '../../../features/measure/measure.actions';
import {
  addCut,
  addCutFailure, addCuts, addCutsFailure, addCutsSuccess,
  addCutSuccess
} from '../../../features/cut/cut.actions';
import {
  addDetection, addDetectionFailure,
  addDetectionSuccess
} from '../../../features/detection/detection.actions';
import {
  addTracker,
  addTrackerElevations, addTrackerElevationsFailure,
  addTrackerElevationsSuccess,
  addTrackerFailure,
  addTrackerImages,
  addTrackerImagesFailure,
  addTrackerImagesSuccess,
  addTrackerSuccess
} from '../../../features/tracker/tracker.actions';
import {
  selectAdvancedFeaturesActivating,
  selectAdvancedFeaturesAvailable,
  selectCurrentSite
} from '../../../features/site/site.selectors';

import { SiteService } from '../../../services/site.service';
import { TranslocoService } from '@jsverse/transloco';


@Component({
    selector: 'world-actions-bar',
    templateUrl: './world-actions-bar.component.html',
    styleUrls: ['./world-actions-bar.component.scss'],
    standalone: false
})
export class WorldActionsBarComponent {

  inspectionModule = InspectionModule;
  isComputing: boolean = false;
  site: Site | undefined | null;
  readonly advancedFeaturesAvailable$ = this.store.select(selectAdvancedFeaturesAvailable);
  readonly advancedFeaturesActivating$ = this.store.select(selectAdvancedFeaturesActivating);
  private drawingMode: DrawingMode | undefined;

  constructor(private store: Store,
              private actions: Actions,
              private siteService: SiteService,
              private translocoService: TranslocoService) {
    this.handleDrawingMode();
    this.handleActions();
    this.handleSite();
  }

  private handleSite(): void {
    this.store.select(selectCurrentSite)
      .pipe(takeUntilDestroyed())
      .subscribe((site: Site | null | undefined) => {
        this.site = site;
      });
  }

  private handleDrawingMode() {
    this.store.select(selectDrawingMode)
      .pipe(takeUntilDestroyed())
      .subscribe((mode: DrawingMode | undefined) => {
        this.drawingMode = mode;
      });
  }

  private handleActions() {
    this.actions.pipe(
      ofType(
        addDetection,
        addMeasureDistance,
        addMeasureArea,
        addCut,
        addCuts,
        addTracker,
        addTrackerImages,
        addTrackerElevations),
      takeUntilDestroyed())
      .subscribe(result => {
        this.isComputing = true;
      });

    this.actions.pipe(
      ofType(
        addDetectionSuccess, addDetectionFailure,
        addMeasureSuccess, addMeasureFailure,
        addCutSuccess, addCutFailure,
        addCutsSuccess, addCutsFailure,
        addTrackerSuccess, addTrackerFailure,
        addTrackerImagesSuccess, addTrackerImagesFailure,
        addTrackerElevationsSuccess, addTrackerElevationsFailure),
      takeUntilDestroyed())
      .subscribe(result => {
        this.isComputing = false;
      });
  }

  onClickFlyToSite(site: Site) {
    this.siteService.flyTo(site);
  }

  drawLine(event: any): void {
    const mode = (this.drawingMode == DrawingMode.MEASURE_LINE) ? undefined : DrawingMode.MEASURE_LINE;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingLine(): boolean {
    return this.drawingMode == DrawingMode.MEASURE_LINE;
  }

  drawPolygon(event: any): void {
    const mode = (this.drawingMode == DrawingMode.MEASURE_POLYGON) ? undefined : DrawingMode.MEASURE_POLYGON;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingPolygon(): boolean {
    return this.drawingMode == DrawingMode.MEASURE_POLYGON;
  }

  drawCut(event: any): void {
    const mode = (this.drawingMode == DrawingMode.CUT_LINE) ? undefined : DrawingMode.CUT_LINE;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingCut(): boolean {
    return this.drawingMode == DrawingMode.CUT_LINE;
  }

  drawMarker(event: any): void {
    const mode = (this.drawingMode == DrawingMode.MARKER_POINT) ? undefined : DrawingMode.MARKER_POINT;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingMarker(): boolean {
    return this.drawingMode == DrawingMode.MARKER_POINT;
  }

  drawNewDetection(event: any): void {
    const mode = (this.drawingMode == DrawingMode.NEW_DETECTION) ? undefined : DrawingMode.NEW_DETECTION;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingNewDetection(): boolean {
    return this.drawingMode == DrawingMode.NEW_DETECTION;
  }

  drawImagesAtPoint(event: any): void {
    const mode = (this.drawingMode == DrawingMode.IMAGES_AT_POINT) ? undefined : DrawingMode.IMAGES_AT_POINT;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingImageAtPoint(): boolean {
    return this.drawingMode == DrawingMode.IMAGES_AT_POINT;
  }

  drawElevation(event: any): void {
    const mode = (this.drawingMode == DrawingMode.ELEVATION) ? undefined : DrawingMode.ELEVATION;
    this.store.dispatch(setDrawingMode({drawingMode: mode}));
  }

  get isDrawingElevation(): boolean {
    return this.drawingMode == DrawingMode.ELEVATION;
  }

  toolTip(advancedFeatureAvailable: boolean | null): string | undefined {
    return (advancedFeatureAvailable) ? undefined : this.translocoService.translate("advanced_features_required");
  }

}
